import { Assets, Container, Sprite, type Texture } from "pixi.js";

// 8-direction sprite system for the local player.
//
// Sprites are pre-rendered from a rigged 3D character (Blender) and deployed
// to /assets/characters/<name>/<anim>/<dir>/frame_NNNN.png. A manifest.json
// at the character root lists frame counts, fps, and loop flag per animation.
//
// API is layer-ready (only "body" used today, future M16 will add equipment
// layers as siblings inside `container`).

export type Direction = "S" | "SE" | "E" | "NE" | "N" | "NW" | "W" | "SW";
type LoopableAnim = "idle" | "walking";
type OneShotAnim = "punching" | "casting" | "attack_1h" | "attack_2h";
type AnimationName = LoopableAnim | OneShotAnim;

interface AnimationConfig {
  frameCount: number;
  fps: number;
  loop: boolean;
}

interface CharacterManifest {
  character: string;
  directions: Direction[];
  animations: Record<AnimationName, AnimationConfig>;
}

// Movement vectors come from keyboard.ts as (cdx, cdy) in game tile space.
// The world projects to iso screen via (tx-ty, tx+ty), so a game vector of
// (+1,+1) ends up moving south on screen — the player expects to see the
// character facing S in that case.
//
// HOWEVER: the Blender camera used to render the beta sprites has a 45° Z
// rotation, so the sprite folder labelled "S" (rendered at character Z=0)
// actually shows the character facing SE on screen. Every label is shifted
// 45° clockwise from its visual facing.
//
// To compensate, we map each "expected on-screen direction" to the sprite
// label rotated 45° counter-clockwise (which then displays correctly).
// Long-term fix: shift Z values in render_sprites.py by -45° and re-render.
const DIRECTION_MAP: Record<string, Direction> = {
  "1,1":   "SE",  // expected S  → load SE label (visually shows S)
  "0,1":   "S",   // expected SW → load S
  "-1,1":  "SW",  // expected W  → load SW
  "-1,0":  "W",   // expected NW → load W
  "-1,-1": "NW",  // expected N  → load NW
  "0,-1":  "N",   // expected NE → load N
  "1,-1":  "NE",  // expected E  → load NE
  "1,0":   "E",   // expected SE → load E
};

export function gameVectorToScreenDirection(cdx: number, cdy: number): Direction | null {
  if (cdx === 0 && cdy === 0) return null;
  return DIRECTION_MAP[`${cdx},${cdy}`] ?? null;
}

// Display height in pixels. Our 256x256 renders have ~30% empty headroom around
// the character, so this corresponds to a visible character of ~85-95 px tall.
// Tuned in playtest 2026-05-22.
const TARGET_HEIGHT = 130;
// Feet anchor: our 256x256 renders have ~15% empty headroom above the head
// and ~10-15% padding below the feet (Blender ortho camera framing). The
// character's actual feet land around 80-85% from the top of the frame.
// Tuned in playtest 2026-05-22 — lower values shift the sprite DOWN on screen.
const FEET_ANCHOR_Y = 0.70;

// Equipment layers composited on top of the body. Each maps to an asset
// category under /assets/equipment/<category>/<itemId>/<body>/. The item
// sprites are rendered from the SAME Blender camera/anim/frame as the body,
// so displaying them at the same size + anchor aligns them pixel-perfect.
// Torso armour (chest) is a LAYER with per-pixel body occlusion baked in (the
// Meshy vest rendered together with the body — see render_sprites.py
// occlude_by_body). The occlusion solves what used to force a body-variant
// (interior showing / arms hidden), so it composites cleanly like the others.
export type LayerSlot = "legs" | "chest" | "helmet" | "main_hand" | "off_hand";
const LAYER_CATEGORY: Record<LayerSlot, string> = {
  legs: "legs",
  chest: "chest",
  helmet: "helms",
  main_hand: "weapons",
  off_hand: "shields",
};

// Base z-order (Pixi sortableChildren; higher = on top) for the body + layers.
const BODY_Z = 10;
const LAYER_Z: Record<LayerSlot, number> = {
  legs: 11, // leg armour: above body, below the torso tunic (its hem overlaps the waist)
  chest: 12, // torso armour: above body, below helmet + held items
  helmet: 16,
  main_hand: 17,
  off_hand: 17,
};
// A held item flips BEHIND the body for directions where its hand is on the
// camera-far side of the torso, so the body occludes it (whole-sprite
// approximation — the per-layer pipeline can't do per-pixel occlusion, so the
// item shows where it extends past the silhouette and hides where it overlaps).
//
// The camera looks from the south. The right-hand weapon reads IN FRONT for the
// eastern/southern visual facings {S, SE, E, NE} and BEHIND for the western/
// northern ones {N, NW, W, SW}. The off-hand (left hand) is the mirror across
// the body, so it goes behind for the eastern visual facings {N, NE, E, SE}.
// These VISUAL facings map to the SPRITE LABELS below via the 45°-shifted
// DIRECTION_MAP (see top of file):
//   visual N→NW · NW→W · W→SW · SW→S · NE→N · E→NE · SE→E
// → main_hand behind = {NW, W, SW, S}; off_hand behind = {NW, N, NE, E}.
// (off_hand is the mirror prediction — verify/tune in-game once a shield ships.)
const LAYER_BEHIND_DIRECTIONS: Partial<Record<LayerSlot, ReadonlySet<Direction>>> = {
  main_hand: new Set<Direction>(["NW", "W", "SW", "S"]),
  off_hand: new Set<Direction>(["NW", "N", "NE", "E"]),
};
const LAYER_BEHIND_Z = BODY_Z - 1;

// Items whose sprites already have per-pixel body occlusion baked into the alpha
// (rendered together with the body — see render_sprites.py occlude_by_body).
// They must ALWAYS composite in front of the body: the parts hidden behind the
// body are already transparent, and the visible parts belong in front. So they
// skip the whole-sprite behind-flip below (which would wrongly hide them).
const ITEM_BAKED_OCCLUSION: ReadonlySet<string> = new Set<string>([
  "axe_iron",
  "sword_iron",
  "shield_wood",
  "leather_chest",
  "leather_pants",
]);

// Relative depth between the two HELD hands when both are equipped (sword in the
// right hand + shield in the left). Per-pixel occlusion handles each item vs the
// BODY, but not the two items vs EACH OTHER (separate layers), so we z-order them
// per direction from the bone depth vs the camera. These SPRITE-LABEL directions
// are where the RIGHT hand is closer to the camera (main-hand weapon draws over
// the shield); the complement is where the shield is in front. NW is a near-tie
// in the raw depth and is assigned to the shield (the off-hand wins NW + W, per
// in-game judgement — the shield reads better in front for those facings).
const MAINHAND_FRONT_DIRECTIONS = new Set<Direction>(["E", "NE", "N"]);
const HELD_FRONT_Z = 18; // the held item that is nearer the camera this facing
const HELD_BACK_Z = 17; // the other held item (still above body + helmet)

export interface PlayerSpriteOptions {
  body: string;
  layers?: Partial<Record<LayerSlot, string | null>>;
}

interface Layer {
  sprite: Sprite;
  itemId: string | null;
  textures: Record<string, Record<string, Texture[]>> | null;
}

export class PlayerSpriteController {
  readonly container = new Container();
  private readonly sprite: Sprite;
  // bodyName = which character's BODY frames are showing (can be a variant like
  // "human_m_leather_chest"). equipBodyName = the BASE body the equipment layers
  // were rendered against (always the plain body, e.g. "human_m") — equipment
  // shares the skeleton so the base frames align on any body variant.
  private bodyName = "";
  private equipBodyName = "";

  // Equipment layers (helmet + weapon + shield today; boots/cape follow the
  // same pattern).
  private readonly layers: Record<LayerSlot, Layer> = {
    legs: { sprite: new Sprite(), itemId: null, textures: null },
    chest: { sprite: new Sprite(), itemId: null, textures: null },
    helmet: { sprite: new Sprite(), itemId: null, textures: null },
    main_hand: { sprite: new Sprite(), itemId: null, textures: null },
    off_hand: { sprite: new Sprite(), itemId: null, textures: null },
  };

  private manifest: CharacterManifest | null = null;
  // textures[anim][direction] = Texture[] (one entry per frame)
  private readonly textures: Record<string, Record<string, Texture[]>> = {};

  private currentAnim: AnimationName = "idle";
  private currentDirection: Direction = "S";
  private currentFrame = 0;
  private frameElapsedMs = 0;
  private lastUpdateMs = 0;

  // One-shot state — punching plays through, then we return to whichever
  // loopable state (idle/walking) is appropriate based on continued input.
  private oneShotActive = false;
  private postOneShotAnim: LoopableAnim = "idle";

  private constructor() {
    this.container.label = "player-sprite";
    this.container.sortableChildren = true; // z-order by sprite.zIndex
    this.sprite = new Sprite();
    this.sprite.anchor.set(0.5, FEET_ANCHOR_Y);
    this.sprite.alpha = 0;
    this.sprite.zIndex = BODY_Z;
    this.container.addChild(this.sprite);

    // Layer sprites — z-order via zIndex (direction-dependent for some, see
    // applyCurrentTexture). Held weapon can flip behind the body when back-facing.
    for (const slot of Object.keys(this.layers) as LayerSlot[]) {
      const s = this.layers[slot].sprite;
      s.anchor.set(0.5, FEET_ANCHOR_Y);
      s.alpha = 0;
      s.zIndex = LAYER_Z[slot];
      this.container.addChild(s);
    }
  }

  // Async factory: preloads every (anim × direction × frame) texture for the
  // requested character. Returns once all textures are in the Pixi Assets cache.
  static async create(opts: PlayerSpriteOptions): Promise<PlayerSpriteController> {
    const controller = new PlayerSpriteController();
    controller.equipBodyName = opts.body; // equipment frames live under the base body
    await controller.loadCharacter(opts.body);
    if (opts.layers) {
      for (const slot of Object.keys(opts.layers) as LayerSlot[]) {
        await controller.setLayer(slot, opts.layers[slot] ?? null);
      }
    }
    return controller;
  }

  private async loadCharacter(name: string): Promise<void> {
    this.bodyName = name;
    const manifestUrl = `/assets/characters/${name}/manifest.json`;
    const res = await fetch(manifestUrl);
    if (!res.ok) throw new Error(`Manifest fetch failed: ${manifestUrl} (${res.status})`);
    const manifest = (await res.json()) as CharacterManifest;
    this.manifest = manifest;

    for (const anim of Object.keys(manifest.animations) as AnimationName[]) {
      this.textures[anim] = {};
      const cfg = manifest.animations[anim];
      for (const dir of manifest.directions) {
        const urls: string[] = [];
        for (let i = 1; i <= cfg.frameCount; i++) {
          const padded = String(i).padStart(4, "0");
          urls.push(`/assets/characters/${name}/${anim}/${dir}/frame_${padded}.png`);
        }
        // Pixi Assets caches by URL, so subsequent PlayerSpriteController
        // instances for the same character will resolve from cache instantly.
        const textures = await Promise.all(urls.map((u) => Assets.load<Texture>(u)));
        this.textures[anim][dir] = textures;
      }
    }

    this.applyCurrentTexture();
  }

  // Swap the BODY variant in place (e.g. when torso armour is equipped, the
  // body becomes "human_m_leather_chest" which has the armour baked in with
  // correct 3D occlusion over the arms). Keeps the current anim/dir/frame and
  // all equipment layers — equipment lookups stay pinned to equipBodyName, so
  // the held items keep aligning. No-op if already showing that body.
  async setBody(name: string): Promise<void> {
    if (name === this.bodyName) return;
    await this.loadCharacter(name);
  }

  // Equip / unequip an item on a layer. itemId === null clears the layer.
  // Loads the item's textures (shares the body's animation set, so we reuse
  // the body manifest for frame counts) then composites it each frame.
  async setLayer(slot: LayerSlot, itemId: string | null): Promise<void> {
    const layer = this.layers[slot];
    if (itemId === null) {
      layer.itemId = null;
      layer.textures = null;
      layer.sprite.alpha = 0;
      layer.sprite.visible = false;
      return;
    }
    if (layer.itemId === itemId && layer.textures) return; // already loaded
    layer.itemId = itemId;
    layer.textures = await this.loadEquipmentTextures(LAYER_CATEGORY[slot], itemId);
    this.applyCurrentTexture();
  }

  private async loadEquipmentTextures(
    category: string,
    itemId: string,
  ): Promise<Record<string, Record<string, Texture[]>>> {
    const manifest = this.manifest;
    if (!manifest) throw new Error("loadEquipmentTextures called before body manifest loaded");
    const result: Record<string, Record<string, Texture[]>> = {};
    for (const anim of Object.keys(manifest.animations) as AnimationName[]) {
      result[anim] = {};
      const cfg = manifest.animations[anim];
      for (const dir of manifest.directions) {
        const urls: string[] = [];
        for (let i = 1; i <= cfg.frameCount; i++) {
          const padded = String(i).padStart(4, "0");
          urls.push(
            `/assets/equipment/${category}/${itemId}/${this.equipBodyName}/${anim}/${dir}/frame_${padded}.png`,
          );
        }
        try {
          result[anim][dir] = await Promise.all(urls.map((u) => Assets.load<Texture>(u)));
        } catch {
          // This animation has no rendered frames for the item yet (e.g. the
          // helmet hasn't been rendered for the attack anims). Leave it empty —
          // applyCurrentTexture hides the layer when no frame is available.
        }
      }
    }
    return result;
  }

  // Call each time the player moves (or continues a sustained move). cdx/cdy
  // are sign-normalised to {-1, 0, 1} so this works whether keyboard or
  // pointer-walk is the source.
  setMoving(cdx: number, cdy: number): void {
    const newDir = gameVectorToScreenDirection(cdx, cdy);
    if (newDir !== null) {
      this.currentDirection = newDir;
    }
    // Set the state to return to once any active one-shot completes.
    this.postOneShotAnim = "walking";
    if (this.oneShotActive) return;
    if (this.currentAnim !== "walking") {
      this.currentAnim = "walking";
      this.currentFrame = 0;
      this.frameElapsedMs = 0;
      this.applyCurrentTexture();
    }
  }

  // Call when the player stops moving (no input, lerp completed).
  setIdle(): void {
    this.postOneShotAnim = "idle";
    if (this.oneShotActive) return;
    if (this.currentAnim !== "idle") {
      this.currentAnim = "idle";
      this.currentFrame = 0;
      this.frameElapsedMs = 0;
      this.applyCurrentTexture();
    }
  }

  // One-shot animation trigger. Plays through to its end frame regardless of
  // movement, then returns to whichever loopable state postOneShotAnim points
  // to (which is kept up to date by setMoving/setIdle while the one-shot plays).
  //
  // Optionally pass (targetDx, targetDy) in game tile space (target - player)
  // to override the facing direction so the character looks at the attacked
  // or cast target rather than keeping its last movement facing.
  private triggerOneShot(anim: OneShotAnim, targetDx?: number, targetDy?: number): void {
    if (targetDx !== undefined && targetDy !== undefined) {
      const newDir = gameVectorToScreenDirection(
        Math.sign(targetDx),
        Math.sign(targetDy),
      );
      if (newDir !== null) this.currentDirection = newDir;
    }
    this.oneShotActive = true;
    this.currentAnim = anim;
    this.currentFrame = 0;
    this.frameElapsedMs = 0;
    this.applyCurrentTexture();
  }

  triggerPunch(targetDx?: number, targetDy?: number): void {
    this.triggerOneShot("punching", targetDx, targetDy);
  }

  triggerCast(targetDx?: number, targetDy?: number): void {
    this.triggerOneShot("casting", targetDx, targetDy);
  }

  // Debug helpers — temporary until weapon-aware attack selection lands.
  triggerAttack1h(targetDx?: number, targetDy?: number): void {
    this.triggerOneShot("attack_1h", targetDx, targetDy);
  }

  triggerAttack2h(targetDx?: number, targetDy?: number): void {
    this.triggerOneShot("attack_2h", targetDx, targetDy);
  }

  // Per-frame tick. Advances the frame index based on elapsed ms and the
  // current animation's fps from the manifest.
  update(now: number): void {
    if (!this.manifest) return;
    const cfg = this.manifest.animations[this.currentAnim];
    if (!cfg) return;

    const dt = this.lastUpdateMs === 0 ? 0 : now - this.lastUpdateMs;
    this.lastUpdateMs = now;
    if (dt <= 0) return;

    const frameDurMs = 1000 / cfg.fps;
    this.frameElapsedMs += dt;

    let frameChanged = false;
    while (this.frameElapsedMs >= frameDurMs) {
      this.frameElapsedMs -= frameDurMs;
      this.currentFrame++;
      frameChanged = true;
      if (this.currentFrame >= cfg.frameCount) {
        if (cfg.loop) {
          this.currentFrame = 0;
        } else {
          // One-shot finished. Restore the appropriate loopable state.
          this.oneShotActive = false;
          this.currentAnim = this.postOneShotAnim;
          this.currentFrame = 0;
          this.frameElapsedMs = 0;
          break;
        }
      }
    }

    if (frameChanged) this.applyCurrentTexture();
  }

  private applyCurrentTexture(): void {
    const animTextures = this.textures[this.currentAnim];
    if (!animTextures) return;
    const dirTextures = animTextures[this.currentDirection];
    if (!dirTextures || dirTextures.length === 0) return;
    const idx = Math.min(this.currentFrame, dirTextures.length - 1);
    const tex = dirTextures[idx];
    if (!tex) return;
    this.sprite.texture = tex;
    const aspect = tex.width / tex.height;
    this.sprite.height = TARGET_HEIGHT;
    this.sprite.width = TARGET_HEIGHT * aspect;
    this.sprite.alpha = 1;

    // Composite each equipped layer on the same anim/dir/frame. Item sprites
    // share the body's 256×256 canvas from the same camera, so identical
    // size + anchor = pixel-perfect overlay.
    for (const slot of Object.keys(this.layers) as LayerSlot[]) {
      const layer = this.layers[slot];
      // Direction-dependent z-order: a held item (weapon/shield) flips behind
      // the body for the directions where its hand is on the camera-far side.
      const bakedOcclusion = layer.itemId !== null && ITEM_BAKED_OCCLUSION.has(layer.itemId);
      const behindDirs = bakedOcclusion ? undefined : LAYER_BEHIND_DIRECTIONS[slot];
      if (bakedOcclusion) {
        // Always in front of the body (occlusion baked). For the two HELD hands,
        // also resolve sword-vs-shield depth per direction (see constants).
        if (slot === "main_hand" || slot === "off_hand") {
          const mainFront = MAINHAND_FRONT_DIRECTIONS.has(this.currentDirection);
          const inFront = slot === "main_hand" ? mainFront : !mainFront;
          layer.sprite.zIndex = inFront ? HELD_FRONT_Z : HELD_BACK_Z;
        } else {
          layer.sprite.zIndex = LAYER_Z[slot];
        }
      } else if (behindDirs) {
        layer.sprite.zIndex = behindDirs.has(this.currentDirection)
          ? LAYER_BEHIND_Z
          : LAYER_Z[slot];
      }
      const layerDir = layer.textures?.[this.currentAnim]?.[this.currentDirection];
      const layerTex = layerDir?.[Math.min(this.currentFrame, (layerDir.length ?? 1) - 1)];
      if (layer.textures && layerTex) {
        layer.sprite.texture = layerTex;
        layer.sprite.height = TARGET_HEIGHT;
        layer.sprite.width = TARGET_HEIGHT * (layerTex.width / layerTex.height);
        layer.sprite.alpha = 1;
        layer.sprite.visible = true;
      } else {
        layer.sprite.alpha = 0;
        layer.sprite.visible = false;
      }
    }
  }

  destroy(): void {
    // Textures intentionally stay in Pixi Assets cache — they'll be reused
    // for any future PlayerSpriteController instance with the same character.
    this.container.destroy({ children: true });
  }
}
